import markerSDK, { type MarkerSdk } from '@marker.io/browser'

let markerWidget: MarkerSdk | null = null
let markerVisibilitySuppressed = false

/**
 * Initialize Marker.io widget for bug reporting and feedback
 */
export async function initMarker() {
  const projectId = import.meta.env.VITE_MARKER_PROJECT_ID

  if (!projectId) {
    console.warn('Marker.io project ID not configured')
    return
  }

  try {
    markerWidget = await markerSDK.loadWidget({
      project: projectId,
    })
    markerWidget.on('show', () => {
      if (markerVisibilitySuppressed) {
        markerWidget?.hide()
      }
    })
    if (markerVisibilitySuppressed) {
      markerWidget.hide()
    }
  } catch (error) {
    console.error('Failed to initialize Marker.io:', error)
  }
}

/**
 * Set the reporter information for Marker.io
 */
export function setMarkerUser(user: {
  id: string
  email?: string
  name?: string
}) {
  if (!markerWidget) {
    return
  }

  try {
    markerWidget.setReporter({
      email: user.email || '',
      fullName: user.name || '',
    })
  } catch (error) {
    console.error('Failed to set Marker.io reporter:', error)
  }
}

/**
 * Capture a screenshot and open the Marker.io feedback form
 */
export function captureMarkerFeedback() {
  if (!markerWidget) {
    console.warn('Marker.io widget not initialized')
    return
  }

  try {
    markerWidget.capture('fullscreen')
  } catch (error) {
    console.error('Failed to capture Marker.io feedback:', error)
  }
}

/**
 * Show the Marker.io widget
 */
export function showMarkerWidget() {
  markerVisibilitySuppressed = false

  if (!markerWidget) {
    return
  }

  try {
    markerWidget.show()
  } catch (error) {
    console.error('Failed to show Marker.io widget:', error)
  }
}

/**
 * Hide the Marker.io widget
 */
export function hideMarkerWidget() {
  markerVisibilitySuppressed = true

  if (!markerWidget) {
    return
  }

  try {
    markerWidget.hide()
  } catch (error) {
    console.error('Failed to hide Marker.io widget:', error)
  }
}

/**
 * Unload the Marker.io widget
 */
export function unloadMarker() {
  if (!markerWidget) {
    return
  }

  try {
    markerWidget.unload()
    markerWidget = null
  } catch (error) {
    console.error('Failed to unload Marker.io widget:', error)
  }
}

/**
 * Check if Marker.io widget is loaded
 */
export function isMarkerReady(): boolean {
  return markerWidget !== null
}
